import ErrorOutline from '@mui/icons-material/ErrorOutline';
import TaskAlt from '@mui/icons-material/TaskAlt';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import SvgIcon from '@mui/material/SvgIcon';
import Typography from '@mui/material/Typography';
import { SxProps, Theme } from '@mui/system';

import { hexToRgba } from '~utils/utilFunctions';

type CustomAlertVariants = 'success' | 'error';

interface CustomAlertProps {
  message: string;
  sx?: SxProps<Theme>;
  variant: CustomAlertVariants;
}

export default function CustomAlert({
  children,
  message,
  sx,
  variant,
}: React.PropsWithChildren<CustomAlertProps>) {
  const theme = useTheme();

  const propsByVariant: Record<
    CustomAlertVariants,
    { icon: typeof SvgIcon; color: typeof theme.palette.error }
  > = {
    error: { color: theme.palette.error, icon: ErrorOutline },
    success: { color: theme.palette.success, icon: TaskAlt },
  };

  const props = propsByVariant[variant];

  return (
    <Box
      sx={[
        {
          bgcolor: hexToRgba(props.color.light, 0.1),
          border: `solid 1px ${hexToRgba(props.color.main, 0.2)}`,
          borderRadius: 1,
          display: 'grid',
          gap: 1.5,
          gridTemplateColumns: 'auto 1fr',
          p: 1.5,
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <props.icon sx={{ fontSize: '18px', mt: '1px' }} />

      <Box
        sx={{
          alignItems: 'flex-start',
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
        }}
      >
        <Typography sx={{ fontSize: '13px' }}>{message}</Typography>

        {children}
      </Box>
    </Box>
  );
}
